// src/pages/Admin/CohortConfigPage.jsx
// Dynamic configuration UI for cohort schedules and per-cohort question sets.
// Route: /admin/cohort-config
// Add to sidebar: key='/admin/cohort-config', icon=<SettingOutlined/>, label='Cohort Config'

import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Input, Select, Switch, Tag, Tooltip, Spin,
  message as antMsg, Popconfirm, Modal,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined,
  SaveOutlined, ReloadOutlined, DragOutlined,
  QuestionCircleOutlined, CheckCircleOutlined,
  SettingOutlined, CopyOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import { addLog } from './SystemLogPage';

// ── Design tokens ────────────────────────────────────────────────
const C = {
  navy:'#1B2D6B', blue:'#1E6FBF', lblue:'#EAF1F8',
  green:'#16a34a', lgreen:'#F0FDF4',
  red:'#DC2626',   lred:'#FEF2F2',
  amber:'#B45309', lamber:'#FFFBEB',
  teal:'#0891b2',  lteal:'#ecfeff',
  purple:'#7c3aed',lpurple:'#f5f3ff',
  text1:'#1a2d4a', text2:'#4a6580', text3:'#7a96b0',
  border:'#DCE6F0', surf:'#F5F8FC', white:'#FFFFFF',
};

const ANSWER_TYPE_OPTIONS = [
  { value:'yesno',   label:'✅ Yes / No / NA',          desc:'Standard yes/no question' },
  { value:'rating5', label:'⭐ Rating 1–5',              desc:'Numeric scale with stars' },
  { value:'choice',  label:'🔘 Multiple Choice',         desc:'Custom options (Daily, etc.)' },
];

const ANSWER_TYPE_COLOR = {
  yesno:   { color:C.green,  bg:C.lgreen  },
  rating5: { color:C.amber,  bg:C.lamber  },
  choice:  { color:C.purple, bg:C.lpurple },
};

// ── Predefined question sets (standard 12-question survey) ─────────
const STANDARD_QUESTIONS = [
  { key:'q1',  label:'Do you clearly understand your current role and responsibilities?', answerType:'yesno',   choices:[] },
  { key:'q2',  label:'Have you got your iPad (Charger & Cover) in working condition?',   answerType:'yesno',   choices:[] },
  { key:'q3',  label:'How comfortable are you using all the systems (VCS, Axis DB)?',    answerType:'rating5', choices:[] },
  { key:'q4',  label:'Do you have clarity of Company policies (Work Norms, Mediclaim, Leave)?', answerType:'yesno', choices:[] },
  { key:'q5',  label:'Do you have your Doctor list & Chemist list?',                     answerType:'yesno',   choices:[] },
  { key:'q6',  label:'Do you have your Product list?',                                   answerType:'yesno',   choices:[] },
  { key:'q7',  label:'Was your ABM/Manager present during the first 3 days of induction?', answerType:'yesno', choices:[] },
  { key:'q8',  label:'Are you able to create your daily plan independently?',            answerType:'rating5', choices:[] },
  { key:'q9',  label:'Are you comfortable presenting products/services to customers independently?', answerType:'yesno', choices:[] },
  { key:'q10', label:'How do you report your calls?',                                    answerType:'choice',  choices:['Daily','Call by Call'] },
  { key:'q11', label:'Have you been introduced to your marketing team?',                 answerType:'yesno',   choices:[] },
  { key:'q12', label:'Have you faced any challenges where you need support?',            answerType:'yesno',   choices:[] },
];

// Predefined cohort schedules
const PREDEFINED_SCHEDULES = {
  pratham: {
    scheduleKey: 'pratham',
    label: 'Pratham Schedule',
    questionsCategory: 'CALL_QUESTIONS',
    stages: [
      { key:'day10',  day:10,  label:'Day 10',  hasQuestions:true  },
      { key:'day30',  day:30,  label:'Day 30',  hasQuestions:false },
      { key:'day60',  day:60,  label:'Day 60',  hasQuestions:false },
      { key:'day105', day:105, label:'Day 105', hasQuestions:false },
      { key:'day150', day:150, label:'Day 150', hasQuestions:false },
      { key:'day180', day:180, label:'Day 180', hasQuestions:false },
    ],
  },
  campus: {
    scheduleKey: 'campus',
    label: 'Campus Schedule',
    questionsCategory: 'CALL_QUESTIONS',
    stages: [
      { key:'day30',  day:30,  label:'Day 30',  hasQuestions:true  },
      { key:'day60',  day:60,  label:'Day 60',  hasQuestions:false },
      { key:'day90',  day:90,  label:'Day 90',  hasQuestions:false },
      { key:'day120', day:120, label:'Day 120', hasQuestions:false },
      { key:'day150', day:150, label:'Day 150', hasQuestions:false },
      { key:'day180', day:180, label:'Day 180', hasQuestions:false },
    ],
  },
};

const emptyStage = () => ({
  _id: Math.random().toString(36).slice(2),
  key: '', day: '', label: '', hasQuestions: false,
});

const emptyQuestion = () => ({
  _id: Math.random().toString(36).slice(2),
  key: '', label: '', answerType: 'yesno', choices: [],
  sortOrder: 0,
});

// ── Small reusable components ─────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div style={{
    background:C.white, borderRadius:14,
    border:`1px solid ${C.border}`,
    boxShadow:'0 1px 4px rgba(0,0,0,0.04)',
    padding:'20px 24px', ...style,
  }}>{children}</div>
);

const SectionTitle = ({ icon, title, sub, extra }) => (
  <div style={{ display:'flex', alignItems:'center', gap:10,
    marginBottom:18, flexWrap:'wrap' }}>
    {icon && <span style={{ fontSize:20 }}>{icon}</span>}
    <div style={{ flex:1 }}>
      <div style={{ fontSize:15, fontWeight:800, color:C.text1 }}>{title}</div>
      {sub && <div style={{ fontSize:12, color:C.text3, marginTop:2 }}>{sub}</div>}
    </div>
    {extra}
  </div>
);

// ═══════════════════════════════════════════════════════════════════
export default function CohortConfigPage() {
  const [schedules,     setSchedules]     = useState([]);
  const [qCategories,   setQCategories]   = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [activeTab,     setActiveTab]     = useState('schedules'); // 'schedules' | 'questions'
  const [activeSchedId, setActiveSchedId] = useState(null); // selected schedule for editing
  const [activeQCat,    setActiveQCat]    = useState(null); // selected question category
  const [questions,     setQuestions]     = useState([]);   // questions for activeQCat
  const [newSchedModal, setNewSchedModal] = useState(false);
  const [newSchedForm,  setNewSchedForm]  = useState({ scheduleKey:'', label:'' });
  const [newCatModal,   setNewCatModal]   = useState(false);
  const [newCatName,    setNewCatName]    = useState('');

  // ── Seed predefined data (Pratham + Campus + shared questions) ──
  async function seedPredefined() {
    setSaving(true);
    try {
      // 1. Save shared question set CALL_QUESTIONS
      await api.post('/admin/config/questions/CALL_QUESTIONS', {
        questions: STANDARD_QUESTIONS.map((q, i) => ({ ...q, sortOrder: i })),
      });

      // 2. Create Pratham schedule if missing
      const hasPratham = schedules.some(s => s.scheduleKey === 'pratham');
      if (!hasPratham) {
        await api.post('/admin/config/schedules', {
          ...PREDEFINED_SCHEDULES.pratham,
          stages: PREDEFINED_SCHEDULES.pratham.stages,
        });
      }

      // 3. Create Campus schedule if missing
      const hasCampus = schedules.some(s => s.scheduleKey === 'campus');
      if (!hasCampus) {
        await api.post('/admin/config/schedules', {
          ...PREDEFINED_SCHEDULES.campus,
          stages: PREDEFINED_SCHEDULES.campus.stages,
        });
      }

      await api.post('/admin/config/reload');
      antMsg.success('✅ Predefined configuration loaded — Pratham, Campus & shared questions saved');
      addLog('CONFIG','SUCCESS','Predefined config seeded — Pratham, Campus, 12 shared questions');
      await loadAll();
    } catch (e) {
      antMsg.error('Seed failed: ' + (e.message || ''));
    } finally { setSaving(false); }
  }

  // ── Load schedules and question categories ────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, qRes] = await Promise.all([
        api.get('/admin/config/schedules'),
        api.get('/admin/config/question-categories'),
      ]);
      const scheds = sRes.data?.data ?? sRes.data ?? [];
      const cats   = qRes.data?.data ?? qRes.data ?? [];
      setSchedules(scheds.map(s => ({
        ...s,
        stages: (s.stages || []).map(st => ({ ...st, _id: st._id || Math.random().toString(36).slice(2) })),
      })));
      setQCategories(cats);
      if (scheds.length && !activeSchedId) setActiveSchedId(scheds[0].id);
      if (cats.length   && !activeQCat)    setActiveQCat(cats[0]);
    } catch (e) {
      antMsg.error('Failed to load config: ' + (e.message || ''));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Load questions when active category changes
  useEffect(() => {
    if (!activeQCat) return;
    api.get(`/admin/config/questions/${encodeURIComponent(activeQCat)}`)
      .then(res => {
        const rows = res.data?.data ?? res.data ?? [];
        setQuestions(rows.map(q => ({ ...q, _id: q._id || Math.random().toString(36).slice(2) })));
      })
      .catch(() => setQuestions([]));
  }, [activeQCat]);

  // ── Reload cache ──────────────────────────────────────────────
  async function reloadCache() {
    try {
      await api.post('/admin/config/reload');
      antMsg.success('✅ Cache reloaded — changes are live');
      addLog('CACHE','SUCCESS','Cache reloaded — all schedules and questions now live');
    } catch(e) {
      antMsg.error('Failed to reload cache');
      addLog('CACHE','ERROR','Cache reload failed', e?.message);
    }
  }

  // ── Active schedule ───────────────────────────────────────────
  const activeSched = schedules.find(s => s.id === activeSchedId);

  function updateActiveSched(patch) {
    setSchedules(prev => prev.map(s => s.id === activeSchedId ? { ...s, ...patch } : s));
  }

  function updateStage(stageId, patch) {
    updateActiveSched({
      stages: activeSched.stages.map(st =>
        st._id === stageId ? { ...st, ...patch } : st
      ),
    });
  }

  function addStage() {
    updateActiveSched({ stages: [...(activeSched?.stages||[]), emptyStage()] });
  }

  function removeStage(stageId) {
    updateActiveSched({ stages: activeSched.stages.filter(st => st._id !== stageId) });
  }

  // ── Save schedule ─────────────────────────────────────────────
  async function saveSchedule() {
    if (!activeSched) return;
    const errs = [];
    if (!activeSched.label.trim()) errs.push('Schedule label is required');
    activeSched.stages.forEach((st, i) => {
      if (!st.key.trim())   errs.push(`Stage ${i+1}: key is required`);
      if (!st.day)          errs.push(`Stage ${i+1}: day number is required`);
      if (!st.label.trim()) errs.push(`Stage ${i+1}: label is required`);
    });
    if (errs.length) { antMsg.error(errs[0]); return; }

    setSaving(true);
    try {
      const payload = {
        label: activeSched.label,
        stages: activeSched.stages.map(({ _id, ...st }) => ({ ...st, day: +st.day })),
        questionsCategory: activeSched.questionsCategory || null,
        isActive: activeSched.isActive !== false,
      };
      if (activeSched.id && typeof activeSched.id === 'number') {
        await api.put(`/admin/config/schedules/${activeSched.id}`, payload);
      } else {
        const res = await api.post('/admin/config/schedules', {
          scheduleKey: activeSched.scheduleKey, ...payload,
        });
        setSchedules(prev => prev.map(s => s.id === activeSchedId
          ? { ...s, id: res.data.id } : s));
        setActiveSchedId(res.data.id);
      }
      antMsg.success('✅ Schedule saved');
      addLog('SCHEDULE','SUCCESS',`Schedule saved: ${activeSched?.label}`, { stages: payload.stages?.length, questionsCategory: payload.questionsCategory });
      await reloadCache();
    } catch (e) {
      antMsg.error('Save failed: ' + (e.message || ''));
    } finally { setSaving(false); }
  }

  // ── Delete schedule ───────────────────────────────────────────
  async function deleteSchedule(id) {
    await api.delete(`/admin/config/schedules/${id}`);
    setSchedules(prev => prev.filter(s => s.id !== id));
    setActiveSchedId(schedules.find(s => s.id !== id)?.id || null);
    await reloadCache();
    antMsg.success('Schedule deleted');
  }

  // ── Create new schedule ───────────────────────────────────────
  async function createSchedule() {
    if (!newSchedForm.scheduleKey.trim() || !newSchedForm.label.trim()) {
      antMsg.error('Key and label are required'); return;
    }
    const tempId = 'new_' + Date.now();
    const newS = {
      id: tempId,
      scheduleKey: newSchedForm.scheduleKey.toLowerCase().trim(),
      label:       newSchedForm.label,
      stages:      [],
      questionsCategory: `QUESTIONS_${newSchedForm.scheduleKey.toUpperCase().trim()}`,
      isActive:    true,
    };
    setSchedules(prev => [...prev, newS]);
    setActiveSchedId(tempId);
    setNewSchedModal(false);
    setNewSchedForm({ scheduleKey:'', label:'' });
    antMsg.info('Schedule created — add stages then Save');
  }

  // ── Q section ─────────────────────────────────────────────────
  function updateQuestion(qId, patch) {
    setQuestions(prev => prev.map(q => q._id === qId ? { ...q, ...patch } : q));
  }

  function addQuestion() {
    const next = emptyQuestion();
    next.key = `q${questions.length + 1}`;
    next.sortOrder = questions.length;
    setQuestions(prev => [...prev, next]);
  }

  function removeQuestion(qId) {
    setQuestions(prev => prev.filter(q => q._id !== qId)
      .map((q, i) => ({ ...q, sortOrder: i })));
  }

  function moveQuestion(qId, dir) {
    const idx = questions.findIndex(q => q._id === qId);
    if (dir === 'up'   && idx === 0) return;
    if (dir === 'down' && idx === questions.length - 1) return;
    const arr = [...questions];
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    setQuestions(arr.map((q, i) => ({ ...q, sortOrder: i })));
  }

  async function saveQuestions() {
    if (!activeQCat) return;
    const errs = questions.map((q,i) => !q.label.trim() && `Q${i+1}: label is required`).filter(Boolean);
    if (errs.length) { antMsg.error(errs[0]); return; }

    setSaving(true);
    try {
      await api.post(`/admin/config/questions/${encodeURIComponent(activeQCat)}`, {
        questions: questions.map(({ _id, ...q }) => q),
      });
      antMsg.success('✅ Questions saved');
      addLog('QUESTIONS','SUCCESS',`Question set saved: ${activeQCat}`, { count: questions.length });
      await reloadCache();
    } catch (e) {
      antMsg.error('Save failed: ' + (e.message || ''));
    } finally { setSaving(false); }
  }

  async function createCategory() {
    const key = `QUESTIONS_${newCatName.toUpperCase().trim()}`;
    if (!newCatName.trim()) { antMsg.error('Name required'); return; }
    if (qCategories.includes(key)) { antMsg.error('Already exists'); return; }
    setQCategories(prev => [...prev, key]);
    setActiveQCat(key);
    setQuestions([]);
    setNewCatModal(false);
    setNewCatName('');
    antMsg.info('Category created — add questions then Save');
  }

  // ── RENDER ────────────────────────────────────────────────────
  return (
    <div style={{ background:'#f0f4f8', minHeight:'100vh' }}>

      {/* ── Page header ── */}
      <div style={{
        background:`linear-gradient(135deg,${C.navy} 0%,#2a4a9f 100%)`,
        padding:'20px 28px',
        boxShadow:'0 4px 20px rgba(27,45,107,0.25)',
      }}>
        <div style={{ display:'flex', justifyContent:'space-between',
          alignItems:'center', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontSize:22, fontWeight:800, color:'#fff',
              display:'flex', alignItems:'center', gap:10 }}>
              <SettingOutlined/> Cohort Configuration
            </div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)', marginTop:3 }}>
              Manage call schedules and Q&A question sets dynamically — no code changes needed
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <Button icon={<SettingOutlined/>} onClick={seedPredefined} loading={saving}
              style={{ borderRadius:8, background:'rgba(255,255,255,0.15)',
                border:'1px solid rgba(255,255,255,0.35)', color:'#fff', fontWeight:600 }}>
              Load Predefined Config
            </Button>
            <Button icon={<ReloadOutlined/>} onClick={reloadCache}
              style={{ borderRadius:8, background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.25)', color:'#fff', fontWeight:600 }}>
              Reload Live Cache
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding:'24px 28px 60px' }}>

        {/* ── How it works banner ── */}
        <div style={{
          background:'linear-gradient(135deg,#eff6ff,#f0fdf4)',
          border:`1px solid ${C.blue}33`,
          borderRadius:14, padding:'16px 20px', marginBottom:24,
          display:'flex', gap:16, flexWrap:'wrap',
        }}>
          {[
            { icon:'📅', title:'1. Create Schedule', desc:'Define call stages with day numbers (e.g. Day-10, Day-30). Mark one stage as Q&A.' },
            { icon:'❓', title:'2. Create Questions', desc:'Build a question set with Yes/No, Rating 1-5, or Choice questions.' },
            { icon:'🔗', title:'3. Link Them', desc:'In the schedule editor, pick the question set. Multiple cohorts can share the same set (e.g. Pratham + Campus both use CALL_QUESTIONS).' },
            { icon:'🔄', title:'4. Reload Cache', desc:'Click "Reload Live Cache" — changes are live instantly, no server restart.' },
          ].map((s,i) => (
            <div key={i} style={{ flex:'1', minWidth:180 }}>
              <div style={{ fontSize:18, marginBottom:4 }}>{s.icon}</div>
              <div style={{ fontSize:12, fontWeight:800, color:C.text1 }}>{s.title}</div>
              <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{s.desc}</div>
            </div>
          ))}
        </div>

        {/* ── Tabs ── */}
        <div style={{ display:'flex', gap:0, marginBottom:24,
          background:C.white, borderRadius:12,
          border:`1px solid ${C.border}`, overflow:'hidden',
          boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
          {[
            { key:'schedules', label:'📅 Call Schedules', sub:'Stages & Q&A mapping' },
            { key:'questions', label:'❓ Question Sets',  sub:'Per-cohort questionnaires' },
          ].map(t => {
            const active = activeTab === t.key;
            return (
              <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                flex:1, padding:'14px 20px', cursor:'pointer',
                border:'none', borderBottom:active?`3px solid ${C.navy}`:'none',
                background:active?C.white:C.surf, textAlign:'left',
                borderRight:`1px solid ${C.border}`,
              }}>
                <div style={{ fontSize:14, fontWeight:800,
                  color:active?C.navy:C.text2 }}>{t.label}</div>
                <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{t.sub}</div>
              </button>
            );
          })}
        </div>

        {loading
          ? <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div>
          : activeTab === 'schedules'
            ? <SchedulesPanel
                schedules={schedules}
                activeSchedId={activeSchedId}
                activeSched={activeSched}
                qCategories={qCategories}
                saving={saving}
                onSelect={setActiveSchedId}
                onNew={() => setNewSchedModal(true)}
                onDelete={deleteSchedule}
                onUpdate={updateActiveSched}
                onUpdateStage={updateStage}
                onAddStage={addStage}
                onRemoveStage={removeStage}
                onSave={saveSchedule}
              />
            : <QuestionsPanel
                qCategories={qCategories}
                activeQCat={activeQCat}
                questions={questions}
                saving={saving}
                onSelectCat={setActiveQCat}
                onNewCat={() => setNewCatModal(true)}
                onAdd={addQuestion}
                onRemove={removeQuestion}
                onUpdate={updateQuestion}
                onMove={moveQuestion}
                onSave={saveQuestions}
                onSeedStandard={() => {
                  setQuestions(STANDARD_QUESTIONS.map((q,i) => ({
                    ...q, _id: Math.random().toString(36).slice(2), sortOrder:i,
                  })));
                  antMsg.info('Standard questions loaded — click Save Questions to persist');
                }}
                schedules={schedules}
              />
        }
      </div>

      {/* ── New Schedule modal ── */}
      <Modal open={newSchedModal} onCancel={() => setNewSchedModal(false)}
        title="Create New Cohort Schedule" footer={null} width={480}>
        <div style={{ display:'flex', flexDirection:'column', gap:14, padding:'8px 0' }}>
          <div>
            <label style={{ fontSize:12, fontWeight:700, color:C.text2,
              display:'block', marginBottom:5 }}>
              Schedule Key <span style={{ color:C.red }}>*</span>
            </label>
            <Input value={newSchedForm.scheduleKey}
              onChange={e => setNewSchedForm(p => ({ ...p, scheduleKey:e.target.value }))}
              placeholder="e.g. galaxy (must match cohortName prefix)"
              style={{ borderRadius:8 }}/>
            <div style={{ fontSize:11, color:C.text3, marginTop:4 }}>
              Lowercase, no spaces. Employees with cohortName starting with this key will use this schedule.
            </div>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:700, color:C.text2,
              display:'block', marginBottom:5 }}>
              Display Label <span style={{ color:C.red }}>*</span>
            </label>
            <Input value={newSchedForm.label}
              onChange={e => setNewSchedForm(p => ({ ...p, label:e.target.value }))}
              placeholder="e.g. Galaxy Schedule"
              style={{ borderRadius:8 }}/>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
            <Button onClick={() => setNewSchedModal(false)}>Cancel</Button>
            <Button type="primary" icon={<PlusOutlined/>} onClick={createSchedule}
              style={{ background:C.navy }}>
              Create Schedule
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── New Question Category modal ── */}
      <Modal open={newCatModal} onCancel={() => setNewCatModal(false)}
        title="Create New Question Set" footer={null} width={440}>
        <div style={{ display:'flex', flexDirection:'column', gap:14, padding:'8px 0' }}>
          <div>
            <label style={{ fontSize:12, fontWeight:700, color:C.text2,
              display:'block', marginBottom:5 }}>
              Cohort Name <span style={{ color:C.red }}>*</span>
            </label>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <span style={{ color:C.text3, fontSize:12, fontFamily:'monospace',
                background:C.surf, padding:'5px 10px', borderRadius:6,
                border:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>
                QUESTIONS_
              </span>
              <Input value={newCatName}
                onChange={e => setNewCatName(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g,''))}
                placeholder="GALAXY"
                style={{ borderRadius:8, fontFamily:'monospace', fontWeight:700 }}/>
            </div>
            <div style={{ fontSize:11, color:C.text3, marginTop:4 }}>
              Will create category <code>QUESTIONS_{newCatName||'...'}</code>
            </div>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
            <Button onClick={() => setNewCatModal(false)}>Cancel</Button>
            <Button type="primary" icon={<PlusOutlined/>} onClick={createCategory}
              style={{ background:C.navy }}>
              Create Question Set
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SCHEDULES PANEL
// ═══════════════════════════════════════════════════════════════════
function SchedulesPanel({
  schedules, activeSchedId, activeSched, qCategories, saving,
  onSelect, onNew, onDelete, onUpdate, onUpdateStage,
  onAddStage, onRemoveStage, onSave,
}) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:20 }}>

      {/* Left: schedule list */}
      <div>
        <div style={{ display:'flex', justifyContent:'space-between',
          alignItems:'center', marginBottom:12 }}>
          <span style={{ fontSize:13, fontWeight:700, color:C.text1 }}>
            Cohort Types
          </span>
          <Button size="small" icon={<PlusOutlined/>} onClick={onNew}
            style={{ borderRadius:8, background:C.navy, color:'#fff',
              border:'none', fontWeight:600 }}>
            New
          </Button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {schedules.map(s => {
            const active = s.id === activeSchedId;
            const qaStage = s.stages?.find(st => st.hasQuestions);
            return (
              <div key={s.id} onClick={() => onSelect(s.id)}
                style={{
                  padding:'12px 14px', borderRadius:12, cursor:'pointer',
                  background:active ? C.navy : C.white,
                  border:`1.5px solid ${active ? C.navy : C.border}`,
                  boxShadow:active ? `0 4px 14px ${C.navy}33` : '0 1px 3px rgba(0,0,0,0.05)',
                  transition:'all .15s',
                }}>
                <div style={{ display:'flex', justifyContent:'space-between',
                  alignItems:'center' }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:800,
                      color:active?'#fff':C.text1 }}>
                      {s.label}
                    </div>
                    <div style={{ fontSize:11,
                      color:active?'rgba(255,255,255,.6)':C.text3,
                      marginTop:2 }}>
                      <code style={{ fontSize:10 }}>{s.scheduleKey}</code>
                      {' · '}{s.stages?.length||0} stages
                    </div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:4,
                    alignItems:'flex-end' }}>
                    {qaStage && (
                      <Tag style={{ fontSize:9, borderRadius:20, padding:'1px 6px',
                        background:active?'rgba(255,255,255,.2)':'#eff6ff',
                        color:active?'#fff':C.blue,
                        border:`1px solid ${active?'rgba(255,255,255,.3)':'#bfdbfe'}` }}>
                        Q&A: {qaStage.label}
                      </Tag>
                    )}
                    <Switch size="small" checked={s.isActive !== false}
                      onClick={e => e.stopPropagation()}
                      onChange={v => onUpdate({ isActive:v })}/>
                  </div>
                </div>
              </div>
            );
          })}
          {schedules.length === 0 && (
            <div style={{ textAlign:'center', padding:32, color:C.text3,
              background:C.white, borderRadius:12,
              border:`1px dashed ${C.border}` }}>
              No schedules yet.<br/>
              <span style={{ color:C.blue, cursor:'pointer' }}
                onClick={onNew}>Create one</span>
            </div>
          )}
        </div>
      </div>

      {/* Right: schedule editor */}
      <div>
        {!activeSched
          ? <div style={{ textAlign:'center', padding:80, color:C.text3,
              background:C.white, borderRadius:14,
              border:`1px solid ${C.border}` }}>
              Select a schedule to edit
            </div>
          : (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {/* Basic info */}
              <Card>
                <SectionTitle icon="📋" title="Schedule Details"
                  extra={
                    <div style={{ display:'flex', gap:8 }}>
                      <Popconfirm title="Delete this schedule?"
                        description="All stage data will be removed."
                        onConfirm={() => onDelete(activeSched.id)}
                        okText="Delete" okButtonProps={{ danger:true }}>
                        <Button size="small" danger icon={<DeleteOutlined/>}
                          style={{ borderRadius:8 }}>
                          Delete
                        </Button>
                      </Popconfirm>
                      <Button type="primary" size="small" icon={<SaveOutlined/>}
                        loading={saving} onClick={onSave}
                        style={{ borderRadius:8, background:C.navy }}>
                        Save Schedule
                      </Button>
                    </div>
                  }/>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                  <div>
                    <label style={{ fontSize:12, fontWeight:700, color:C.text2,
                      display:'block', marginBottom:5 }}>
                      Display Label *
                    </label>
                    <Input value={activeSched.label}
                      onChange={e => onUpdate({ label:e.target.value })}
                      placeholder="e.g. Pratham Schedule"
                      style={{ borderRadius:8 }}/>
                  </div>
                  <div>
                    <label style={{ fontSize:12, fontWeight:700, color:C.text2,
                      display:'block', marginBottom:5 }}>
                      Schedule Key (read-only)
                    </label>
                    <Input value={activeSched.scheduleKey} disabled
                      style={{ borderRadius:8, fontFamily:'monospace' }}/>
                  </div>
                  <div style={{ gridColumn:'span 2' }}>
                    <label style={{ fontSize:12, fontWeight:700, color:C.text2,
                      display:'block', marginBottom:5 }}>
                      Question Set
                      <Tooltip title="Employees on this schedule will see these questions during their Q&A call">
                        <QuestionCircleOutlined style={{ marginLeft:6, color:C.text3 }}/>
                      </Tooltip>
                    </label>
                    <Select value={activeSched.questionsCategory || 'CALL_QUESTIONS'}
                      onChange={v => onUpdate({ questionsCategory: v === 'CALL_QUESTIONS' ? null : v })}
                      style={{ width:'100%', borderRadius:8 }}>
                      <Select.Option value="CALL_QUESTIONS">
                        <span>📋 CALL_QUESTIONS</span>
                        <Tag style={{ marginLeft:8, fontSize:10,
                          background:'#eff6ff', color:C.blue, border:'none' }}>
                          shared default
                        </Tag>
                      </Select.Option>
                      {qCategories.filter(c => c !== 'CALL_QUESTIONS').map(c => {
                        const usedBy = schedules.filter(s =>
                          s.id !== activeSched?.id &&
                          (s.questionsCategory === c || (!s.questionsCategory && c === 'CALL_QUESTIONS'))
                        ).map(s => s.label);
                        return (
                          <Select.Option key={c} value={c}>
                            <span>{c}</span>
                            {c === `QUESTIONS_${(activeSched?.scheduleKey||'').toUpperCase()}` && (
                              <Tag style={{ marginLeft:6, fontSize:10,
                                background:'#f0fdf4', color:C.green, border:'none' }}>
                                recommended
                              </Tag>
                            )}
                            {usedBy.length > 0 && (
                              <Tag style={{ marginLeft:6, fontSize:10,
                                background:'#f5f3ff', color:C.purple, border:'none' }}>
                                also used by {usedBy.join(', ')}
                              </Tag>
                            )}
                          </Select.Option>
                        );
                      })}
                    </Select>
                    <div style={{ fontSize:11, color:C.text3, marginTop:4,
                      display:'flex', alignItems:'center', gap:6 }}>
                      💡 Multiple cohorts can share the same question set — select
                      <strong style={{ color:C.blue }}>CALL_QUESTIONS</strong> to use the shared standard set
                    </div>
                  </div>
                </div>
              </Card>

              {/* Stages editor */}
              <Card>
                <SectionTitle icon="📆" title="Call Stages"
                  sub="Define each touchpoint in the 180-day journey"
                  extra={
                    <Button size="small" icon={<PlusOutlined/>} onClick={onAddStage}
                      style={{ borderRadius:8, background:C.blue,
                        color:'#fff', border:'none' }}>
                      Add Stage
                    </Button>
                  }/>

                {/* Stage column headers */}
                <div style={{ display:'grid',
                  gridTemplateColumns:'1fr 80px 120px 110px 40px',
                  gap:10, marginBottom:8, padding:'0 4px' }}>
                  {['Stage Key','Day #','Label','Q&A Call?',''].map(h => (
                    <div key={h} style={{ fontSize:10, fontWeight:700,
                      color:C.text3, textTransform:'uppercase',
                      letterSpacing:'0.5px' }}>{h}</div>
                  ))}
                </div>

                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {(activeSched.stages||[]).map((st, idx) => (
                    <div key={st._id} style={{
                      display:'grid',
                      gridTemplateColumns:'1fr 80px 120px 110px 40px',
                      gap:10, alignItems:'center',
                      background:st.hasQuestions ? '#eff6ff' : C.surf,
                      borderRadius:10, padding:'10px 12px',
                      border:`1.5px solid ${st.hasQuestions ? C.blue+'44' : C.border}`,
                    }}>
                      {/* Key */}
                      <Input size="small"
                        value={st.key}
                        onChange={e => onUpdateStage(st._id, { key:e.target.value.toLowerCase().replace(/\s/g,'') })}
                        placeholder="day10"
                        style={{ borderRadius:6, fontFamily:'monospace', fontWeight:700, fontSize:12 }}/>
                      {/* Day */}
                      <Input size="small" type="number" min={1} max={365}
                        value={st.day}
                        onChange={e => onUpdateStage(st._id, { day:e.target.value })}
                        placeholder="10"
                        style={{ borderRadius:6, textAlign:'center' }}/>
                      {/* Label */}
                      <Input size="small"
                        value={st.label}
                        onChange={e => onUpdateStage(st._id, { label:e.target.value })}
                        placeholder="Day 10"
                        style={{ borderRadius:6 }}/>
                      {/* hasQuestions */}
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <Switch size="small"
                          checked={!!st.hasQuestions}
                          onChange={v => {
                            // Only one stage can have hasQuestions=true
                            const newStages = (activeSched.stages||[]).map(s =>
                              s._id === st._id
                                ? { ...s, hasQuestions:v }
                                : { ...s, hasQuestions: v ? false : s.hasQuestions }
                            );
                            onUpdate({ stages:newStages });
                          }}/>
                        {st.hasQuestions && (
                          <span style={{ fontSize:10, fontWeight:700,
                            color:C.blue }}>Q&A</span>
                        )}
                      </div>
                      {/* Delete */}
                      <Button size="small" type="text" danger
                        icon={<DeleteOutlined/>}
                        onClick={() => onRemoveStage(st._id)}
                        style={{ borderRadius:6 }}/>
                    </div>
                  ))}

                  {(activeSched.stages||[]).length === 0 && (
                    <div style={{ textAlign:'center', padding:24,
                      color:C.text3, fontSize:13,
                      border:`1px dashed ${C.border}`,
                      borderRadius:10, background:C.white }}>
                      No stages yet — click Add Stage
                    </div>
                  )}
                </div>

                {/* Validation hints */}
                <div style={{ marginTop:12, padding:'10px 14px',
                  background:C.surf, borderRadius:10,
                  border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:11, color:C.text2 }}>
                    <strong>Tips:</strong>&nbsp;
                    Stage key must be unique (e.g. <code>day10</code>, <code>day30</code>).
                    &nbsp;Exactly one stage should have Q&A enabled — that's when the questionnaire form opens.
                    &nbsp;Stages auto-sort by day number.
                  </div>
                </div>
              </Card>
            </div>
          )
        }
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// QUESTIONS PANEL
// ═══════════════════════════════════════════════════════════════════
function QuestionsPanel({
  qCategories, activeQCat, questions, saving,
  onSelectCat, onNewCat, onAdd, onRemove, onUpdate, onMove, onSave,
  onSeedStandard, schedules = [],
}) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:20 }}>

      {/* Left: category list */}
      <div>
        <div style={{ display:'flex', justifyContent:'space-between',
          alignItems:'center', marginBottom:12 }}>
          <span style={{ fontSize:13, fontWeight:700, color:C.text1 }}>
            Question Sets
          </span>
          <Button size="small" icon={<PlusOutlined/>} onClick={onNewCat}
            style={{ borderRadius:8, background:C.navy, color:'#fff',
              border:'none', fontWeight:600 }}>
            New
          </Button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {qCategories.map(cat => {
            const active = cat === activeQCat;
            const cohortPart = cat.replace('QUESTIONS_','');
            return (
              <div key={cat} onClick={() => onSelectCat(cat)}
                style={{
                  padding:'12px 14px', borderRadius:12, cursor:'pointer',
                  background:active?C.navy:C.white,
                  border:`1.5px solid ${active?C.navy:C.border}`,
                  boxShadow:active?`0 4px 14px ${C.navy}33`:'0 1px 3px rgba(0,0,0,0.05)',
                  transition:'all .15s',
                }}>
                <div style={{ fontSize:13, fontWeight:800,
                  color:active?'#fff':C.text1 }}>
                  {cat === 'CALL_QUESTIONS' ? '📋 Shared (Default)' : `❓ ${cohortPart}`}
                </div>
                <div style={{ fontSize:11,
                  color:active?'rgba(255,255,255,.6)':C.text3, marginTop:2 }}>
                  <code style={{ fontSize:10 }}>{cat}</code>
                </div>
                {/* Show which cohorts use this question set */}
                {(() => {
                  const users = schedules.filter(s =>
                    s.questionsCategory === cat ||
                    (!s.questionsCategory && cat === 'CALL_QUESTIONS')
                  );
                  return users.length > 0 ? (
                    <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginTop:6 }}>
                      {users.map(s => (
                        <span key={s.id} style={{
                          fontSize:9, fontWeight:700, padding:'1px 6px',
                          borderRadius:20,
                          background:active?'rgba(255,255,255,.2)':'#eff6ff',
                          color:active?'#fff':C.blue,
                          border:`1px solid ${active?'rgba(255,255,255,.3)':'#bfdbfe'}`,
                        }}>{s.label}</span>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
            );
          })}
          {qCategories.length === 0 && (
            <div style={{ textAlign:'center', padding:32, color:C.text3,
              background:C.white, borderRadius:12,
              border:`1px dashed ${C.border}` }}>
              No question sets.<br/>
              <span style={{ color:C.blue, cursor:'pointer' }}
                onClick={onNewCat}>Create one</span>
            </div>
          )}
        </div>
      </div>

      {/* Right: question editor */}
      <div>
        {!activeQCat
          ? <div style={{ textAlign:'center', padding:80, color:C.text3,
              background:C.white, borderRadius:14,
              border:`1px solid ${C.border}` }}>
              Select a question set to edit
            </div>
          : (
            <Card>
              <SectionTitle
                icon="❓"
                title={activeQCat === 'CALL_QUESTIONS' ? 'Shared Questions (Default)' : `Questions: ${activeQCat}`}
                sub={`${questions.length} question${questions.length!==1?'s':''} · used when no per-cohort set is assigned`}
                extra={
                  <div style={{ display:'flex', gap:8 }}>
                    <Button size="small" icon={<PlusOutlined/>} onClick={onAdd}
                      style={{ borderRadius:8, background:C.blue,
                        color:'#fff', border:'none' }}>
                      Add Question
                    </Button>
                    {questions.length === 0 && (
                      <Button size="small" onClick={onSeedStandard}
                        style={{ borderRadius:8, background:C.lgreen,
                          color:C.green, border:`1px solid ${C.green}44`,
                          fontWeight:600 }}>
                        ✨ Use Standard 12 Questions
                      </Button>
                    )}
                    <Button type="primary" size="small" icon={<SaveOutlined/>}
                      loading={saving} onClick={onSave}
                      style={{ borderRadius:8, background:C.navy }}>
                      Save Questions
                    </Button>
                  </div>
                }/>

              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {questions.map((q, idx) => {
                  const atc = ANSWER_TYPE_COLOR[q.answerType] || ANSWER_TYPE_COLOR.yesno;
                  return (
                    <div key={q._id} style={{
                      background:C.surf, borderRadius:12,
                      border:`1.5px solid ${C.border}`,
                      padding:'14px 16px',
                    }}>
                      {/* Row 1: number + key + type + controls */}
                      <div style={{ display:'flex', alignItems:'center',
                        gap:10, marginBottom:10 }}>
                        {/* Number */}
                        <div style={{ width:26, height:26, borderRadius:8,
                          background:C.navy, color:'#fff',
                          display:'flex', alignItems:'center',
                          justifyContent:'center', fontSize:11,
                          fontWeight:800, flexShrink:0 }}>
                          {idx+1}
                        </div>
                        {/* Key */}
                        <Input size="small" value={q.key}
                          onChange={e => onUpdate(q._id, { key:e.target.value })}
                          placeholder="q1"
                          style={{ width:70, borderRadius:6,
                            fontFamily:'monospace', fontWeight:700,
                            fontSize:12 }}/>
                        {/* Answer type */}
                        <Select size="small" value={q.answerType}
                          onChange={v => onUpdate(q._id, { answerType:v, choices:[] })}
                          style={{ width:180 }}>
                          {ANSWER_TYPE_OPTIONS.map(o => (
                            <Select.Option key={o.value} value={o.value}>
                              {o.label}
                            </Select.Option>
                          ))}
                        </Select>
                        <Tag style={{ borderRadius:20, fontSize:10, fontWeight:700,
                          color:atc.color, background:atc.bg,
                          border:`1px solid ${atc.color}44`, flexShrink:0 }}>
                          {q.answerType}
                        </Tag>
                        {/* Move up/down */}
                        <div style={{ marginLeft:'auto', display:'flex', gap:4 }}>
                          <Button size="small" type="text"
                            disabled={idx===0}
                            onClick={() => onMove(q._id,'up')}
                            style={{ fontSize:11, color:C.text3, padding:'0 4px' }}>
                            ↑
                          </Button>
                          <Button size="small" type="text"
                            disabled={idx===questions.length-1}
                            onClick={() => onMove(q._id,'down')}
                            style={{ fontSize:11, color:C.text3, padding:'0 4px' }}>
                            ↓
                          </Button>
                          <Popconfirm title="Remove this question?"
                            onConfirm={() => onRemove(q._id)}
                            okText="Remove" okButtonProps={{ danger:true }}>
                            <Button size="small" type="text" danger
                              icon={<DeleteOutlined/>}/>
                          </Popconfirm>
                        </div>
                      </div>

                      {/* Row 2: question label */}
                      <Input value={q.label}
                        onChange={e => onUpdate(q._id, { label:e.target.value })}
                        placeholder="Enter question text…"
                        style={{ borderRadius:8, marginBottom: q.answerType==='choice'?10:0 }}/>

                      {/* Row 3: choice options (only for choice type) */}
                      {q.answerType === 'choice' && (
                        <div style={{ marginTop:10 }}>
                          <div style={{ fontSize:11, fontWeight:700,
                            color:C.text2, marginBottom:6 }}>
                            Answer Options
                            <span style={{ color:C.text3, fontWeight:400, marginLeft:6 }}>
                              (comma-separated or add one by one)
                            </span>
                          </div>
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap',
                            marginBottom:8 }}>
                            {(q.choices||[]).map((ch, ci) => (
                              <span key={ci} style={{
                                display:'inline-flex', alignItems:'center', gap:4,
                                padding:'3px 10px', borderRadius:20,
                                background:C.lpurple, color:C.purple,
                                border:`1px solid ${C.purple}44`,
                                fontSize:12, fontWeight:600,
                              }}>
                                {ch}
                                <span
                                  onClick={() => onUpdate(q._id, {
                                    choices:(q.choices||[]).filter((_,i)=>i!==ci)
                                  })}
                                  style={{ cursor:'pointer', opacity:.6,
                                    fontSize:14, lineHeight:1 }}>×</span>
                              </span>
                            ))}
                          </div>
                          <Input.Search
                            size="small"
                            placeholder="Type option and press Enter"
                            enterButton="Add"
                            style={{ borderRadius:8 }}
                            onSearch={v => {
                              if (!v.trim()) return;
                              onUpdate(q._id, { choices:[...(q.choices||[]), v.trim()] });
                            }}/>
                          <div style={{ fontSize:11, color:C.text3, marginTop:4 }}>
                            Examples: Daily, Call by Call, Weekly
                          </div>
                        </div>
                      )}

                      {/* Inverted hint */}
                      {q.answerType === 'yesno' && (
                        <div style={{ marginTop:8, fontSize:11, color:C.text3,
                          display:'flex', alignItems:'center', gap:6 }}>
                          <QuestionCircleOutlined/>
                          If "No" is the positive answer (e.g. "Have you faced challenges?"),
                          add the key to <code>INVERTED_KEYS</code> in
                          <code> CohortDay10Summary.jsx</code> and
                          <code> FeedbackQuestionCards.jsx</code>.
                        </div>
                      )}
                    </div>
                  );
                })}

                {questions.length === 0 && (
                  <div style={{ textAlign:'center', padding:40, color:C.text3,
                    fontSize:13, border:`1px dashed ${C.border}`,
                    borderRadius:12, background:C.white }}>
                    No questions yet — click Add Question
                  </div>
                )}
              </div>
            </Card>
          )
        }
      </div>
    </div>
  );
}